import React, { useState } from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaTypography } from '../../styles/theme';
import { VerdictAvatars } from '../household/VerdictAvatars';
import { useHouseholdEaters } from '../../hooks/useHouseholdEaters';
import { parseDayOrInstant } from '../../utils/localDate';
import { useSelector } from 'react-redux';
import { selectRecipeVerdicts } from '../../redux/recipeMetadataSlice';
import type { RootState } from '../../redux/store';

interface CookNote {
  date: string;
  text: string;
  /** uid of the author. Absent on notes written before notes were attributed. */
  by?: string;
}

interface CookedLineProps {
  timesCooked: number;
  /** ISO date of the most recent cook. */
  lastCooked?: string;
  /** Whose verdicts to show beside the journal line. */
  recipeId: string;
  /** Free-form cook notes, most recent first. */
  notes?: CookNote[];
}

function formatFullDate(iso: string, locale: string): string {
  try {
    return parseDayOrInstant(iso).toLocaleDateString(locale, { day: 'numeric', month: 'long' });
  } catch {
    return '';
  }
}

/**
 * The cook journal, in one place: how often, when, who liked it, and what was
 * written about it.
 *
 * The notes used to render from `CookLog` at the very bottom of the recipe —
 * after the video, the ingredients and every cooking step — under their own
 * "Vos notes" heading. So the sentence explaining WHY a meal went well sat a
 * full scroll away from the pills saying WHO liked it, and the page carried two
 * headings for one record. They are the same journal entry; they belong
 * together.
 *
 * Notes are set in Newsreader italic rather than the UI sans: this is something
 * a person wrote, not a data field, and the serif is what says so.
 */
export const CookedLine: React.FC<CookedLineProps> = ({
  timesCooked,
  lastCooked,
  recipeId,
  notes = [],
}) => {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const { table } = useHouseholdEaters(t('household.eaters.selfLabel'));
  const verdicts = useSelector((s: RootState) => selectRecipeVerdicts(s, recipeId));

  const cooked = !!timesCooked && timesCooked >= 1;
  // Who liked it is worth showing even with no cook on record: verdicts live on
  // the recipe, so they outlive a planned meal that was replaced, and a page
  // that hid both hearts read as "nothing was ever said about this" (#795).
  const answered = Object.keys(verdicts).length > 0;
  if (!cooked && !answered && notes.length === 0) return null;

  // Follow the UI language — a French date inside an English line reads broken.
  const locale = i18n.language?.startsWith('fr') ? 'fr-FR' : 'en-US';
  const dateLabel = lastCooked ? formatFullDate(lastCooked, locale) : '';

  const shown = expanded ? notes : notes.slice(0, 1);
  const hidden = notes.length - shown.length;

  /**
   * Whoever wrote it, named the way they are named everywhere else — viewer
   * relative, so you are "Toi" and never your own first name. An unattributed
   * note (written before notes carried an author) shows a date and nothing
   * else, rather than guessing.
   */
  const authorOf = (by?: string): string | null => {
    if (!by) return null;
    return table.find((m) => m.id === by)?.name ?? null;
  };

  return (
    <View testID="cooked-line" style={styles.wrap}>
      <View style={styles.row}>
        {cooked ? (
          <>
            <Ionicons name="flame" size={14} color={Paprika.accent} />
            <Text testID="cooked-line-count" style={styles.text}>
              {t('recipeDetail.cookedTimes', {
                defaultValue: 'Cuisiné {{count}} fois',
                count: timesCooked,
              })}
              {dateLabel
                ? ` · ${t('recipeDetail.cookedLastTime', {
                    defaultValue: 'dernière fois le {{date}}',
                    date: dateLabel,
                  })}`
                : ''}
            </Text>
          </>
        ) : null}
        {/* Who liked it, not how many stars. The derived 1-5 still exists for
            ranking, but is never drawn. */}
        <VerdictAvatars recipeId={recipeId} variant="paper" />
      </View>

      {shown.map((note, i) => {
        const author = authorOf(note.by);
        return (
          <View key={`${note.date}-${i}`} style={styles.note}>
            <Text testID="cooked-line-note" style={styles.noteText}>{note.text}</Text>
            <Text style={styles.noteMeta}>
              {formatFullDate(note.date, locale)}
              {author ? ` · ${author}` : ''}
            </Text>
          </View>
        );
      })}

      {hidden > 0 && (
        <Pressable onPress={() => setExpanded(true)} hitSlop={6} style={styles.moreHit}>
          <Text style={styles.moreText}>
            {t('recipeDetail.moreCookNotes', { count: hidden })}
          </Text>
        </Pressable>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    marginTop: 6,
    gap: 8,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  text: {
    fontFamily: 'Inter-Regular',
    fontSize: 12.5,
    lineHeight: 18,
    color: Paprika.textMuted,
  },
  note: {
    // A quiet rule rather than a card: the note is a continuation of the line
    // above, not a second section competing with it.
    borderLeftWidth: 2,
    borderLeftColor: Paprika.bgSunk,
    paddingLeft: 10,
    marginLeft: 2,
  },
  noteText: {
    fontFamily: 'Newsreader-Italic',
    fontStyle: 'italic',
    fontSize: 14.5,
    lineHeight: 20,
    color: Paprika.textPrimary,
  },
  noteMeta: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
    marginTop: 2,
  },
  moreHit: {
    alignSelf: 'flex-start',
    paddingLeft: 12,
  },
  moreText: {
    ...PaprikaTypography.labelSmall,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    color: Paprika.accent,
  },
});

export default CookedLine;
